import React, { useState, useEffect } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useHotel } from "../../contexts/HotelContext";
import { useTheme } from "../../contexts/ThemeContext";
import { useAuth0 } from "@auth0/auth0-react";
import { useHotelHub } from "../../providers/signalr/HotelHubProvider";
import { useAdminNotificationStore } from "../../stores/adminNotificationStore";
import { toast } from "react-toastify";
import { API_BASE_URL } from "../../services/common/constants";
import { cn } from "../../lib/utils";
import Sidebar from "./Sidebar";
import Topbar from "./Topbar";
import HotelSwitchOverlay from "./HotelSwitchOverlay";
import FloatingChatWidget from "./FloatingChatWidget";
import SubscriptionWarningBanner from "./SubscriptionWarningBanner";
import { serviceRegistry } from "../../services/core/serviceRegistry";
import { REGISTRATION_MODE_KEY } from "./AppBootstrapOverlay";
import { useEnableRealTimeUpdates, useSentimentAlerts } from "../../hooks/admin";
import { usePageSettings } from "../../contexts/PageSettingsContext";
import { logger } from "../../utils/logger";

/**
 * SignalR transfer request notification payload
 */
interface SignalRTransferRequest {
  hotelId?: string;
  roomNumber?: string;
  destinationName?: string;
  customDestinationAddress?: string;
}

interface AdminLayoutProps {
  userPermissions: string[];
  limitToBilling?: boolean;
  subscriptionWarning?: import("../../services/admin/types/billing.types").SubscriptionWarningData | null;
  onRefreshPermissions?: () => void;
}

const AdminLayout: React.FC<AdminLayoutProps> = ({
  userPermissions,
  limitToBilling,
  subscriptionWarning,
  onRefreshPermissions,
}) => {
  const [isSidebarOpen, setIsSidebarOpen] = useState<boolean>(false);
  const [languageKey, setLanguageKey] = useState<number>(0); // Force re-render on language change
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const {
    isAuthenticated,
    user: authUser,
    isLoading,
    getAccessTokenSilently,
  } = useAuth0();
  const { myHotelInfo, isHotelInfoLoading } = useHotel();
  const { theme } = useTheme();

  // Enable real-time updates for React Query
  useEnableRealTimeUpdates();

  // Enable global sentiment alerts (toasts on any admin page when negative sentiment detected)
  // This ensures alerts show regardless of which admin page the user is on
  useSentimentAlerts();

  const { refreshPageSettings } = usePageSettings();

  // Clear registration mode flag when entering the main admin dashboard
  // This ensures future page refreshes don't show dark bootstrap overlay
  useEffect(() => {
    try {
      localStorage.removeItem(REGISTRATION_MODE_KEY);
    } catch {
      // Ignore localStorage errors
    }
  }, []);

  const {
    fetchUnreadChatCount: fetchStoreUnreadChatCount,
    setPendingTransferRequestCount,
    fetchPendingTransferRequestCount,
  } = useAdminNotificationStore();

  const { on, off, isConnected } = useHotelHub();

  // Listen for permission/role changes via SignalR
  useEffect(() => {
    if (!isConnected || !onRefreshPermissions) return;

    const handlePermissionsChanged = (scope: string, affectedStaffUserId?: string | null): void => {
      const myStaffId = myHotelInfo?.staffUserId;

      if (scope === "user" && affectedStaffUserId) {
        if (myStaffId && affectedStaffUserId === myStaffId) {
          onRefreshPermissions();
        }
      } else if (scope === "role") {
        onRefreshPermissions();
      } else if (scope === "pages") {
        onRefreshPermissions();
        refreshPageSettings();
      }
    };

    on("ReceivePermissionsChanged", handlePermissionsChanged);
    return () => {
      off("ReceivePermissionsChanged", handlePermissionsChanged);
    };
  }, [isConnected, on, off, onRefreshPermissions, refreshPageSettings, myHotelInfo?.staffUserId]);

  // Initialize service registry when auth is ready
  useEffect(() => {
    if (isAuthenticated && !isLoading && getAccessTokenSilently) {
      // Initialize service registry if not already initialized
      if (!serviceRegistry.isAdminInitialized()) {
        serviceRegistry.initializeAdmin({
          baseUrl: API_BASE_URL,
          getAccessToken: getAccessTokenSilently,
        });
      }
    }
  }, [isAuthenticated, isLoading, getAccessTokenSilently]);

  // SignalR connection setup
  useEffect(() => {
    if (
      !isHotelInfoLoading &&
      myHotelInfo &&
      myHotelInfo.hotelId &&
      isAuthenticated &&
      !isLoading &&
      serviceRegistry.isAdminInitialized()
    ) {
      fetchStoreUnreadChatCount();
      fetchPendingTransferRequestCount();
    }
  }, [
    isHotelInfoLoading,
    myHotelInfo,
    isAuthenticated,
    isLoading,
    authUser,
    fetchStoreUnreadChatCount,
    fetchPendingTransferRequestCount,
  ]);

  // Set up SignalR event handlers
  useEffect(() => {
    logger.log("[AdminLayout] SignalR setup:", {
      isConnected,
      hotelId: myHotelInfo?.hotelId,
    });
    if (!isConnected || !myHotelInfo?.hotelId) return;

    const hotelIdForSignalR = myHotelInfo.hotelId;

    const handleUnreadChatCountUpdate = (receivedHotelId: string) => {
      if (receivedHotelId === hotelIdForSignalR) {
        fetchStoreUnreadChatCount();
      }
    };

    const handleNewTransferRequest = (transferRequest: SignalRTransferRequest) => {
      logger.log(
        "[AdminLayout] Received new transfer request:",
        transferRequest,
      );
      logger.log(
        "[AdminLayout] Hotel ID from request:",
        transferRequest.hotelId,
      );
      logger.log("[AdminLayout] Expected hotel ID:", hotelIdForSignalR);

      // Check if hotelId matches - but also try without the check since the backend might not send it
      if (
        !transferRequest.hotelId ||
        transferRequest.hotelId === hotelIdForSignalR
      ) {
        // The backend sends destinationName directly
        const destinationName =
          transferRequest.destinationName ||
          transferRequest.customDestinationAddress ||
          "Unknown destination";
        const message = t("signalr_new_transfer_request_message", {
          roomNumber: transferRequest.roomNumber,
          destinationName: destinationName,
          defaultValue: `New transfer request from Room ${transferRequest.roomNumber} to ${destinationName}`,
        });
        logger.log(
          "[AdminLayout] Showing transfer request notification:",
          message,
        );
        toast.info(message);
        // Don't fetch count here - let ReceiveTransferRequestCountUpdate handle it
      } else {
        logger.log("[AdminLayout] Hotel ID mismatch, not showing notification");
      }
    };

    const handleTransferRequestCountUpdate = (newCount: number) => {
      setPendingTransferRequestCount(newCount);
    };

    // Register event handlers
    logger.log(
      "[AdminLayout] Registering SignalR event handlers for transfer requests",
    );
    on("ReceiveUnreadChatCountUpdate", handleUnreadChatCountUpdate);
    on("ReceiveNewTransferRequest", handleNewTransferRequest);
    on("ReceiveNewTransferRequestNotification", handleNewTransferRequest); // Try both event names
    on("ReceiveTransferRequestCountUpdate", handleTransferRequestCountUpdate);

    // Cleanup
    return () => {
      off("ReceiveUnreadChatCountUpdate", handleUnreadChatCountUpdate);
      off("ReceiveNewTransferRequest", handleNewTransferRequest);
      off("ReceiveNewTransferRequestNotification", handleNewTransferRequest);
      off(
        "ReceiveTransferRequestCountUpdate",
        handleTransferRequestCountUpdate,
      );
    };
  }, [
    isConnected,
    myHotelInfo,
    on,
    off,
    fetchStoreUnreadChatCount,
    fetchPendingTransferRequestCount,
    setPendingTransferRequestCount,
    t,
  ]);

  // Update page title based on route
  useEffect(() => {
    // Page title is not currently used in the component
    // Future enhancement: Could be used in document.title or passed to Topbar
  }, [location]);

  // Listen for language changes to force re-render
  useEffect(() => {
    const handleLanguageChange = () => {
      logger.log("[AdminLayout] Language changed, forcing re-render");
      setLanguageKey((prev) => prev + 1);
    };

    i18n.on("languageChanged", handleLanguageChange);

    return () => {
      i18n.off("languageChanged", handleLanguageChange);
    };
  }, [i18n]);

  // Translation loading is handled by RouteAwareTranslationProvider

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  return (
    <>
      {/* Hotel Switch Overlay - shown when switching between hotels */}
      <HotelSwitchOverlay />

      <div
        className={cn(
          "flex h-screen w-full bg-background overflow-hidden",
          theme === "dark" && "dark",
        )}
      >
        {/* Sidebar - hidden on fullscreen pages like KDS */}
        {location.pathname !== "/admin/kitchen-display" && (
          <Sidebar
            key={`sidebar-${languageKey}`}
            isOpen={isSidebarOpen}
            userPermissions={userPermissions}
            limitToBilling={!!limitToBilling}
            onClose={() => setIsSidebarOpen(false)}
          />
        )}

      {/* Main Content - flex-1 to take remaining space */}
      <div className="flex-1 flex flex-col h-full overflow-hidden">
        {/* Topbar - hidden on fullscreen pages like KDS */}
        {location.pathname !== "/admin/kitchen-display" && (
          <Topbar key={`topbar-${languageKey}`} onMenuClick={toggleSidebar} />
        )}

        {/* Subscription warning banner — shown on every admin page when payment failed or suspended */}
        {location.pathname !== "/admin/kitchen-display" &&
          (subscriptionWarning || limitToBilling) && (
          <SubscriptionWarningBanner
            subscriptionWarning={subscriptionWarning}
            limitToBilling={!!limitToBilling}
          />
        )}

        {/* Page Content */}
        <main className="flex-1 overflow-y-auto main-content-scrollbar bg-muted/10">
          <div className={cn(
            "w-full",
            // Full-screen pages without padding
            (location.pathname === "/admin/welcome" || location.pathname === "/admin/kitchen-display") ? "" : "px-3 py-4 sm:px-5 sm:py-5 lg:px-8 lg:py-8"
          )}>
            <Outlet key={languageKey} context={{ userPermissions }} />
          </div>
        </main>
      </div>

      {/* Floating Chat Widget - shows on all pages except /admin/chat and KDS */}
      {!limitToBilling &&
        location.pathname !== "/admin/chat" &&
        location.pathname !== "/admin/kitchen-display" && <FloatingChatWidget />}
    </div>
    </>
  );
};

export default AdminLayout;
